import { Injectable } from '@angular/core';
import { EducationalFacilityDTO, SchoolAtlasService } from '@ui/shared/util-api';
import { FilterLimitingConfig, FilterOption } from '@ui/shared/util-filter';
import { BehaviorSubject, Observable, combineLatest, distinctUntilChanged, map, noop, shareReplay } from 'rxjs';

import { filterKeys } from '../../definitions/filter-keys';

@Injectable({ providedIn: 'root' })
export class SchoolMapFilterConfigService {
    public readonly educationalBranchesLimitingConfig$: Observable<FilterLimitingConfig>;
    public readonly careServicesLimitingConfig$: Observable<FilterLimitingConfig>;

    private readonly _knownTypeValues$ = new BehaviorSubject<Array<number>>([]);
    private readonly _knownMapItems$ = new BehaviorSubject<Record<number, EducationalFacilityDTO>>({});

    constructor(
        schoolMapApi: SchoolAtlasService,
    ) {
        const limitingConfigs$
            = combineLatest([
                this._knownTypeValues$
                    .pipe(
                        distinctUntilChanged((previous, current) =>
                            JSON.stringify(previous) === JSON.stringify(current),
                        ),
                    ),
                this._knownMapItems$
                    .pipe(
                        distinctUntilChanged((previous, current) =>
                            JSON.stringify(previous) === JSON.stringify(current),
                        ),
                    ),
            ])
                .pipe(
                    map(([types, mapItems]) =>
                        this._createLimitingConfigs(mapItems, { types }),
                    ),
                    shareReplay(1),
                );

        this.educationalBranchesLimitingConfig$
            = limitingConfigs$
                .pipe(
                    map(({ educationalBranches }) => educationalBranches),
                    distinctUntilChanged((previous, current) =>
                        JSON.stringify(previous) === JSON.stringify(current),
                    ),
                    shareReplay(1),
                );

        this.careServicesLimitingConfig$
            = limitingConfigs$
                .pipe(
                    map(({ careServices }) => careServices),
                    distinctUntilChanged((previous, current) =>
                        JSON.stringify(previous) === JSON.stringify(current),
                    ),
                    shareReplay(1),
                );

        /*
         * Initialize the map items once.
         * This is okay to error; If the map items can't be fetched, the app will not show the filters, and a retry button is diplayed.
         * When the app can fetch the map items again, the known map items get updated automatically.
         */
        schoolMapApi.getAllFacilities({})
            .subscribe({
                next: facilities => { this.updateMapItems(facilities.data); },
                error: noop,
            });
    }

    /*
     * Add any new types, but don't remove the old ones.
     * The limiting config builds on all types, not just the ones that are currently shown.
     */
    public updateTypeValues(typeValues: Array<FilterOption>): void {
        this._knownTypeValues$.next(
            Array.from(
                new Set(
                    this._knownTypeValues$.value
                        .concat(
                            typeValues.map(typeValue => typeValue.value as number),
                        ),
                ),
            ),
        );
    }

    /*
     * Add any new map items and replace old ones (in case they have changed in the meantime), but don't remove any.
     * The limiting config builds on all map items, not just the ones that are currently shown.
     */
    public updateMapItems(mapItems: Array<EducationalFacilityDTO>): void {
        const knownMapItems = { ...this._knownMapItems$.getValue() };

        mapItems
            .forEach(mapItem => {
                knownMapItems[mapItem.id] = mapItem;
            });

        this._knownMapItems$.next(knownMapItems);
    }

    private _createLimitingConfigs(
        mapItems: Record<number, EducationalFacilityDTO>,
        filterValues: { types: Array<number> },
    ): { educationalBranches: FilterLimitingConfig; careServices: FilterLimitingConfig } {
        const filterKey = filterKeys.types;

        const educationalBranchesFilterConfig: Record<number, Array<number>> = {};
        const careServicesFilterConfig: Record<number, Array<number>> = {};

        const addValueToLimitingConfig = (
            config: Record<number, Array<number>>,
            key: number,
            allowedValues: Array<number>,
        ): void => {
            config[key]
                = Array.from(
                    new Set(
                        (config[key] ?? new Array<number>())
                            .concat(...allowedValues),
                    ),
                );
        };

        /*
         * Create an empty entry for each existing type value.
         * This limits the allowed options for types that don't exist in the map items.
         */
        filterValues.types
            .forEach(filterValue => {
                addValueToLimitingConfig(educationalBranchesFilterConfig, filterValue, []);
                addValueToLimitingConfig(careServicesFilterConfig, filterValue, []);
            });

        // From all map items, add the allowed options to the matching type in the config.
        Object.values(mapItems)
            .forEach(mapItem => {
                addValueToLimitingConfig(educationalBranchesFilterConfig, mapItem.type.id, mapItem.branches.map(branch => branch.id));
                addValueToLimitingConfig(careServicesFilterConfig, mapItem.type.id, mapItem.services.map(service => service.id));
            });

        return {
            educationalBranches: { [filterKey]: educationalBranchesFilterConfig },
            careServices: { [filterKey]: careServicesFilterConfig },
        };
    }
}
